import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Users,
  Zap,
  Target,
  ChevronDown,
  MessageSquare,
  Save
} from "lucide-react";

/* ── Reusable Custom Select (matches app-wide design) ── */
const CustomSelect = ({ options, value, onChange, style, className = "", badgeMap = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className={`relative ${className}`} style={style} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs border rounded-lg transition-colors cursor-pointer font-medium h-[36px] ${
          isOpen
            ? "bg-white border-zinc-900 ring-2 ring-zinc-900/10 shadow-2xs"
            : "bg-zinc-50/50 border-zinc-200 text-zinc-900 hover:bg-zinc-50 hover:border-zinc-300"
        }`}
      >
        <span className="truncate flex items-center gap-1.5">
          {value}
          {badgeMap[value] && (
            <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold rounded bg-zinc-100 text-zinc-700 border border-zinc-200 leading-none">
              {badgeMap[value]}
            </span>
          )}
        </span>
        <ChevronDown
          size={13}
          className={`text-zinc-400 transition-transform duration-200 flex-shrink-0 ml-1.5 ${isOpen ? "rotate-180 text-zinc-900" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-1.5 bg-white border border-zinc-200 rounded-lg shadow-lg z-[100] overflow-hidden py-1">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => { onChange(opt); setIsOpen(false); }}
              className={`px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors flex items-center justify-between gap-2 ${
                value === opt
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              <span className="truncate">{opt}</span>
              {badgeMap[opt] && (
                <span className={`flex-shrink-0 inline-flex px-1.5 py-0.5 text-[9px] font-semibold rounded leading-none ${
                  value === opt
                    ? "bg-white/20 text-white"
                    : "bg-zinc-100 text-zinc-700 border border-zinc-200"
                }`}>
                  {badgeMap[opt]}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Component ── */
function ConditionEditor({ campaignData, onBack, onSave, customParams = [] }) {
  const containerRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(820);
  const [isResizing, setIsResizing] = useState(false);
  const [conditions, setConditions] = useState(() => {
    const cond = campaignData?.conditions || {};
    return {
      audience: cond.audience || "all",
      delay: cond.delay !== undefined ? cond.delay : 5,
      priority: cond.priority || 0,
      matchType: cond.matchType || "all",
      rules: cond.rules || [],
    };
  });

  const startResizing = useCallback(() => setIsResizing(true), []);
  const stopResizing = useCallback(() => setIsResizing(false), []);

  const resize = useCallback(
    (e) => {
      if (isResizing && containerRef.current) {
        const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
        const maxWidth = window.innerWidth - 100;
        if (newWidth > 320 && newWidth < maxWidth) setSidebarWidth(newWidth);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizing);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, resize, stopResizing]);

  /* ── Data helpers ── */
  const properties = [...customParams, "Custom..."];
  const propertyBadgeMap = {};
  const operators = ["is", "is not", "greater than", "less than", "contains"];

  const applyAudiencePreset = (type) => {
    setConditions((prev) => ({ ...prev, audience: type }));
  };

  const handleAddRule = () =>
    setConditions((prev) => ({
      ...prev,
      rules: [...prev.rules, { property: properties[0] || "Custom...", operator: "is", value: "" }],
    }));

  const handleRemoveRule = (index) =>
    setConditions((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));

  const updateRule = (index, field, value) => {
    const newRules = [...conditions.rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setConditions((prev) => ({ ...prev, rules: newRules }));
  };

  const audienceOptions = [
    { key: "all", label: "All Visitors", icon: <Users size={13} /> },
    { key: "first_time", label: "New Visitors", icon: <Zap size={13} /> },
    { key: "returning", label: "Returning", icon: <Target size={13} /> },
  ];

  return (
    <div
      className="flex flex-col font-['Inter',sans-serif]"
      style={{ height: "calc(100vh - 84px)", background: "#ffffff" }}
      ref={containerRef}
    >
      {/* ── Header ── */}
      <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer flex-shrink-0"
            title="Back to Content"
          >
            <ArrowLeft size={15} />
          </button>
          <div>
            <h1 className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight">
              Targeting & Behavior
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">{campaignData?.name || "Untitled Campaign"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 border border-zinc-200 transition-colors cursor-pointer"
          >
            Back to Content
          </button>
          <button
            type="button"
            onClick={() => onSave({ ...campaignData, conditions })}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors bg-zinc-900 text-white hover:bg-zinc-800 cursor-pointer shadow-xs active:scale-[0.98]"
          >
            <Save size={14} />
            <span>Finish & Save</span>
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Left Sidebar ── */}
        <aside
          className="bg-white border-r border-zinc-200 overflow-y-auto flex-shrink-0"
          style={{ width: sidebarWidth }}
        >
          <div className="p-6 sm:p-8 space-y-6">
            {/* ── Section 1: Audience ── */}
            <section className="space-y-3">
              <div>
                <h2 className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Users size={13} className="text-zinc-500" />
                  <span>Select Audience</span>
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Choose which visitor segment will see this campaign.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 bg-zinc-100/70 p-1 rounded-xl border border-zinc-200/70">
                {audienceOptions.map(({ key, label, icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyAudiencePreset(key)}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                      conditions.audience === key
                        ? "bg-white text-zinc-900 shadow-2xs border border-zinc-200/80"
                        : "text-zinc-500 hover:text-zinc-800"
                    }`}
                  >
                    {icon}
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* ── Section 2: Priority ── */}
            <section className="space-y-3">
              <div>
                <h2 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                  Priority
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Determine trigger order when multiple campaigns qualify.
                </p>
              </div>
              <div className="flex gap-2">
                {[0, 1, 2, 3].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setConditions((prev) => ({ ...prev, priority: p }))}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer border ${
                      (conditions.priority || 0) === p
                        ? "bg-zinc-900 text-white border-zinc-900 shadow-2xs"
                        : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
                    }`}
                  >
                    {p === 0 ? "Normal" : `P${p}`}
                  </button>
                ))}
              </div>
            </section>

            {/* ── Section 3: Trigger ── */}
            <section className="space-y-4">
              <div>
                <h2 className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Zap size={13} className="text-zinc-500" />
                  <span>Automatic Trigger</span>
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Define time delays and conditional triggers.
                </p>
              </div>

              {/* Time Delay */}
              <div className="flex items-center justify-between bg-zinc-50/60 px-4 py-3 rounded-xl border border-zinc-200/80">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap size={15} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-zinc-900">Time Delay</div>
                    <div className="text-[11px] text-zinc-500">Wait after page load</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={conditions.delay}
                    onChange={(e) =>
                      setConditions((prev) => ({ ...prev, delay: parseInt(e.target.value) || 0 }))
                    }
                    className="w-16 px-2.5 py-1.5 text-center text-xs font-semibold text-zinc-900 border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-900 transition-colors shadow-2xs"
                  />
                  <span className="text-xs font-medium text-zinc-500">sec</span>
                </div>
              </div>

              {/* Targeting Rules header */}
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                    <Target size={13} className="text-zinc-500" />
                    <span>Targeting Rules</span>
                  </h3>
                </div>

                {/* Column headers */}
                {conditions.rules.length > 0 && (
                  <div className="grid grid-cols-12 gap-2 px-1 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                    <div className="col-span-5">Property</div>
                    <div className="col-span-3">Condition</div>
                    <div className="col-span-3">Value</div>
                    <div className="col-span-1" />
                  </div>
                )}

                {/* Rule cards */}
                <div className="space-y-2">
                  {conditions.rules.map((rule, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-2 items-center bg-white p-2.5 rounded-xl border border-zinc-200 shadow-2xs"
                    >
                      {/* Property */}
                      <div className="col-span-5">
                        {rule.property === "Custom..." || rule.isCustom ? (
                          <div className="relative">
                            <input
                              className="w-full px-3 py-1.5 text-xs font-medium border border-zinc-200 rounded-lg bg-zinc-50/50 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors pr-7 h-[36px]"
                              value={rule.customName || ""}
                              onChange={(e) => updateRule(idx, "customName", e.target.value)}
                              placeholder="Property name..."
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newRules = [...conditions.rules];
                                newRules[idx] = { ...newRules[idx], property: properties[0], isCustom: false };
                                setConditions((prev) => ({ ...prev, rules: newRules }));
                              }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors text-xs cursor-pointer"
                              title="Back to presets"
                            >
                              ↺
                            </button>
                          </div>
                        ) : (
                          <CustomSelect
                            options={properties}
                            value={rule.property}
                            badgeMap={propertyBadgeMap}
                            onChange={(val) => {
                              if (val === "Custom...") {
                                const newRules = [...conditions.rules];
                                newRules[idx] = { ...newRules[idx], property: "Custom...", isCustom: true, customName: "" };
                                setConditions((prev) => ({ ...prev, rules: newRules }));
                              } else {
                                updateRule(idx, "property", val);
                              }
                            }}
                          />
                        )}
                      </div>

                      {/* Operator */}
                      <div className="col-span-3">
                        <CustomSelect
                          options={operators}
                          value={rule.operator}
                          onChange={(val) => updateRule(idx, "operator", val)}
                        />
                      </div>

                      {/* Value */}
                      <div className="col-span-3">
                        <input
                          className="w-full px-3 py-1.5 text-xs font-medium border border-zinc-200 rounded-lg bg-zinc-50/50 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors h-[36px]"
                          value={rule.value}
                          onChange={(e) => updateRule(idx, "value", e.target.value)}
                          placeholder="Value..."
                        />
                      </div>

                      {/* Delete */}
                      <div className="col-span-1 flex justify-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveRule(idx)}
                          className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Delete rule"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add rule button */}
                <button
                  type="button"
                  onClick={handleAddRule}
                  className="w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-zinc-700 bg-zinc-50/50 border border-dashed border-zinc-300 rounded-lg hover:bg-zinc-50 hover:border-zinc-400 transition-colors cursor-pointer"
                >
                  <Plus size={13} />
                  <span>Add Targeting Condition</span>
                </button>
              </div>
            </section>
          </div>
        </aside>

        {/* ── Resizer ── */}
        <div
          className={`w-1 flex-shrink-0 cursor-col-resize relative group transition-colors ${
            isResizing ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-400"
          }`}
          onMouseDown={startResizing}
        />

        {/* ── Right Panel: Summary ── */}
        <main className="flex-1 flex items-center justify-center p-8 bg-zinc-50/50 overflow-auto">
          <div className="text-center max-w-sm w-full font-['Inter',sans-serif]">
            <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
              <div className="w-10 h-10 bg-zinc-100 border border-zinc-200 text-zinc-900 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MessageSquare size={18} />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight mb-1.5">
                Launch Configuration
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Your campaign will show to{" "}
                <span className="font-semibold text-zinc-900">
                  {conditions.audience === "all"
                    ? "all"
                    : conditions.audience === "first_time"
                    ? "first-time"
                    : "returning"}
                </span>{" "}
                visitors after a{" "}
                <span className="font-semibold text-zinc-900">{conditions.delay} second</span> delay.
              </p>

              {conditions.rules.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-zinc-500">
                    <Target size={13} />
                    <span>
                      {conditions.rules.length} Custom Targeting Rule{conditions.rules.length > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              )}

              {(conditions.priority || 0) > 0 && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-zinc-100 text-zinc-800 border border-zinc-200 rounded-lg text-xs font-semibold">
                  Priority Level: P{conditions.priority}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default ConditionEditor;
